import {
  type PointerEvent,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FileViewerInitialLoadOverlay,
  FileViewerLoadingSpinner,
  FileViewerResizeOverlay,
  FileViewerToolbar,
  FileViewerToolbarButton,
  FileViewerToolbarDivider,
  FileViewerToolbarSection,
  FileViewerToolbarText,
} from 'components/FileViewer/viewer-controls';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { useZoom } from 'context/ZoomContext';
import { Logger } from 'utilities';
import {
  IMAGE_INITIAL_RESIZE_OVERLAY_DELAY_MS,
  IMAGE_MAX_ZOOM,
  IMAGE_MIN_ZOOM,
  IMAGE_RESIZE_OVERLAY_MS,
  IMAGE_RESIZE_THRESHOLD_PX,
  IMAGE_VIEWER_PADDING,
  IMAGE_ZOOM_STEP,
} from './ImageViewer.constants';
import { ImageViewerProps } from './ImageViewer.props';
import {
  ImageViewerCanvas,
  ImageViewerContainer,
  ImageViewerImage,
  ImageViewerScrollContainer,
  ImageViewerStateContainer,
  ImageViewerWrapper,
} from './ImageViewer.styles';
import type { ImagePanSession, ImageViewerDimensions, ImageZoomAnchor } from './ImageViewer.types';
import { areDimensionsEqual, clampRatio, getImageContainerElement } from './ImageViewer.utils';

const ImageViewer = ({
  src,
  base64Content,
  isSourceLoading = false,
  compensateAppZoom = false,
  contentType = 'image/jpeg',
  alt = 'Image',
  animateOnMount = true,
  toolbarSize = 'compact',
}: ImageViewerProps) => {
  const { zoomLevel } = useZoom();
  const [imageData, setImageData] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [naturalDimensions, setNaturalDimensions] = useState<ImageViewerDimensions>();
  const [viewportDimensions, setViewportDimensions] = useState<ImageViewerDimensions>();
  const [zoomScale, setZoomScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [isInitialImageLayoutSettled, setIsInitialImageLayoutSettled] = useState(false);
  const [isResizePreviewPending, setIsResizePreviewPending] = useState(false);
  const [scrollContainerElement, setScrollContainerElement] = useState<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const panSessionRef = useRef<ImagePanSession | null>(null);
  const pendingZoomAnchorRef = useRef<ImageZoomAnchor | null>(null);
  const hasCompletedInitialRenderRef = useRef(false);
  const isImageResizeOverlayEnabledRef = useRef(false);
  const scrollContainerBoxDimensionsRef = useRef<ImageViewerDimensions | undefined>(undefined);
  const resizeOverlayEnableTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const resizeOverlayTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const syncNaturalDimensions = useCallback((imageElement: HTMLImageElement) => {
    if (imageElement.naturalWidth <= 0 || imageElement.naturalHeight <= 0) {
      return;
    }

    const nextDimensions = {
      height: imageElement.naturalHeight,
      width: imageElement.naturalWidth,
    };

    setNaturalDimensions(currentDimensions =>
      areDimensionsEqual(currentDimensions, nextDimensions) ? currentDimensions : nextDimensions
    );
  }, []);

  const commitViewportDimensions = useCallback(
    (nextDimensions: ImageViewerDimensions, nextBoxDimensions: ImageViewerDimensions) => {
      const previousBoxDimensions = scrollContainerBoxDimensionsRef.current;
      const hasMeaningfulResize =
        previousBoxDimensions &&
        (Math.abs(nextBoxDimensions.width - previousBoxDimensions.width) > IMAGE_RESIZE_THRESHOLD_PX ||
          Math.abs(nextBoxDimensions.height - previousBoxDimensions.height) > IMAGE_RESIZE_THRESHOLD_PX);

      if (hasMeaningfulResize && hasCompletedInitialRenderRef.current && isImageResizeOverlayEnabledRef.current) {
        setIsResizePreviewPending(true);
        clearTimeout(resizeOverlayTimeoutRef.current);
        resizeOverlayTimeoutRef.current = setTimeout(() => {
          setIsResizePreviewPending(false);
        }, IMAGE_RESIZE_OVERLAY_MS);
      }

      scrollContainerBoxDimensionsRef.current = nextBoxDimensions;
      setViewportDimensions(currentDimensions =>
        areDimensionsEqual(currentDimensions, nextDimensions) ? currentDimensions : nextDimensions
      );
    },
    []
  );

  const handleScrollContainerRef = useCallback((scrollContainer: HTMLDivElement | null) => {
    scrollContainerRef.current = scrollContainer;
    setScrollContainerElement(scrollContainer);
  }, []);

  const captureCurrentZoomAnchor = useCallback((): ImageZoomAnchor | null => {
    const scrollContainer = scrollContainerRef.current;
    const imageContainer = getImageContainerElement(imageRef.current);
    if (!scrollContainer || !imageContainer) {
      return null;
    }

    return {
      horizontalRatio:
        imageContainer.offsetWidth > 0
          ? clampRatio(
            (scrollContainer.scrollLeft + scrollContainer.clientWidth / 2 - imageContainer.offsetLeft) /
                imageContainer.offsetWidth
          )
          : 0.5,
      verticalRatio:
        imageContainer.offsetHeight > 0
          ? clampRatio(
            (scrollContainer.scrollTop + scrollContainer.clientHeight / 2 - imageContainer.offsetTop) /
                imageContainer.offsetHeight
          )
          : 0.5,
    };
  }, []);

  const updateZoomScale = useCallback(
    (getNextZoomScale: (currentZoomScale: number) => number) => {
      const nextZoomAnchor = captureCurrentZoomAnchor();
      pendingZoomAnchorRef.current = nextZoomAnchor;

      setZoomScale(currentZoomScale => {
        const nextZoomScale = getNextZoomScale(currentZoomScale);
        if (nextZoomScale === currentZoomScale) {
          return currentZoomScale;
        }

        return nextZoomScale;
      });
    },
    [captureCurrentZoomAnchor]
  );

  // Load the active image source and reset viewer state for a clean preview transition.
  useEffect(() => {
    setError(undefined);
    setImageData(undefined);
    setNaturalDimensions(undefined);
    setIsInitialImageLayoutSettled(false);
    setIsResizePreviewPending(false);
    setZoomScale(1);
    hasCompletedInitialRenderRef.current = false;
    isImageResizeOverlayEnabledRef.current = false;
    clearTimeout(resizeOverlayEnableTimeoutRef.current);
    clearTimeout(resizeOverlayTimeoutRef.current);

    if (!base64Content && !src) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      if (base64Content) {
        setImageData(`data:${contentType};base64,${base64Content}`);
      } else if (src) {
        setImageData(src);
      }
    } catch (err) {
      Logger.error('Error processing image data:', err);
      setError('Failed to process image data');
    } finally {
      setLoading(false);
    }
  }, [base64Content, isSourceLoading, src, contentType]);

  // Keep 100% zoom mapped to "fit inside the current preview panel".
  useEffect(() => {
    if (!scrollContainerElement) {
      return undefined;
    }

    let frameId: number | null = null;
    const ownerWindow = scrollContainerElement.ownerDocument.defaultView ?? window;
    const measureViewport = () => {
      frameId = null;
      const { height, width } = scrollContainerElement.getBoundingClientRect();
      commitViewportDimensions(
        {
          height: Math.max(1, Math.round(scrollContainerElement.clientHeight)),
          width: Math.max(1, Math.round(scrollContainerElement.clientWidth)),
        },
        {
          height: Math.max(1, Math.round(height)),
          width: Math.max(1, Math.round(width)),
        }
      );
    };
    const scheduleMeasure = () => {
      if (frameId != null) {
        return;
      }

      frameId = ownerWindow.requestAnimationFrame(measureViewport);
    };
    const observer = new ResizeObserver(scheduleMeasure);

    measureViewport();
    observer.observe(scrollContainerElement);
    return () => {
      observer.disconnect();
      if (frameId != null) {
        ownerWindow.cancelAnimationFrame(frameId);
      }
    };
  }, [commitViewportDimensions, scrollContainerElement]);

  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      syncNaturalDimensions(event.currentTarget);
    },
    [syncNaturalDimensions]
  );

  const handleImageRef = useCallback(
    (imageElement: HTMLImageElement | null) => {
      imageRef.current = imageElement;
      if (imageElement?.complete) {
        syncNaturalDimensions(imageElement);
      }
    },
    [syncNaturalDimensions]
  );

  const handleImageError = useCallback(() => {
    setError('Failed to load image');
  }, []);

  // Cached images can complete before React receives the load event, so sync
  // dimensions right after commit and once more on the next frame.
  useLayoutEffect(() => {
    if (!imageData || !imageRef.current) {
      return undefined;
    }

    const imageElement = imageRef.current;
    const ownerWindow = imageElement.ownerDocument.defaultView ?? window;
    let frameId: number | null = null;
    const syncLoadedImage = () => {
      if (frameId != null) {
        frameId = null;
      }
      syncNaturalDimensions(imageElement);
    };

    syncLoadedImage();
    imageElement.addEventListener('load', syncLoadedImage);
    frameId = ownerWindow.requestAnimationFrame(syncLoadedImage);

    return () => {
      imageElement.removeEventListener('load', syncLoadedImage);
      if (frameId != null) {
        ownerWindow.cancelAnimationFrame(frameId);
      }
    };
  }, [imageData, syncNaturalDimensions]);

  // Some data-url images can be decoded by the browser before React observes
  // their load event; keep polling briefly until natural dimensions are ready.
  useEffect(() => {
    if (!imageData || naturalDimensions || !imageRef.current) {
      return undefined;
    }

    const imageElement = imageRef.current;
    const ownerWindow = imageElement.ownerDocument.defaultView ?? window;
    let frameId: number | null = null;
    let attempts = 0;
    const syncUntilReady = () => {
      frameId = null;
      if (imageRef.current !== imageElement) {
        return;
      }

      if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
        syncNaturalDimensions(imageElement);
        return;
      }

      attempts += 1;
      if (attempts < 60) {
        frameId = ownerWindow.requestAnimationFrame(syncUntilReady);
      }
    };

    frameId = ownerWindow.requestAnimationFrame(syncUntilReady);
    return () => {
      if (frameId != null) {
        ownerWindow.cancelAnimationFrame(frameId);
      }
    };
  }, [imageData, naturalDimensions, syncNaturalDimensions]);

  // Clear the resize overlay if the component unmounts before the timeout runs.
  useEffect(
    () => () => {
      clearTimeout(resizeOverlayEnableTimeoutRef.current);
      clearTimeout(resizeOverlayTimeoutRef.current);
    },
    []
  );

  const renderedDimensions = useMemo(() => {
    if (!naturalDimensions || !viewportDimensions) {
      return undefined;
    }

    const availableWidth = Math.max(1, viewportDimensions.width - IMAGE_VIEWER_PADDING);
    const availableHeight = Math.max(1, viewportDimensions.height - IMAGE_VIEWER_PADDING);
    const fitScale = Math.min(availableWidth / naturalDimensions.width, availableHeight / naturalDimensions.height, 1);

    return {
      height: Math.round(naturalDimensions.height * fitScale * zoomScale),
      width: Math.round(naturalDimensions.width * fitScale * zoomScale),
    };
  }, [naturalDimensions, viewportDimensions, zoomScale]);

  const isImageReady = Boolean(imageData && naturalDimensions && renderedDimensions);
  const isImageVisible = isImageReady && isInitialImageLayoutSettled;
  const isImageResizing = isImageVisible && isResizePreviewPending;
  const isPannable = Boolean(
    renderedDimensions &&
      viewportDimensions &&
      (renderedDimensions.width > viewportDimensions.width || renderedDimensions.height > viewportDimensions.height)
  );
  const zoomPercentage = Math.round(zoomScale * 100);
  const isComfortableToolbar = toolbarSize === 'comfortable';
  const shouldApplyToolbarScale = isComfortableToolbar || compensateAppZoom;
  const toolbarZoomLevel = compensateAppZoom ? zoomLevel : 1;
  const getToolbarPx = useCallback(
    (compactValue: number, comfortableValue = compactValue) =>
      getZoomAdjustedPx(isComfortableToolbar ? comfortableValue : compactValue, toolbarZoomLevel),
    [isComfortableToolbar, toolbarZoomLevel]
  );
  const toolbarStyle = useMemo(
    () =>
      shouldApplyToolbarScale
        ? {
          gap: getToolbarPx(8, 10),
          padding: `${getToolbarPx(6, 7)} ${getToolbarPx(10)} ${getToolbarPx(8)}`,
        }
        : undefined,
    [getToolbarPx, shouldApplyToolbarScale]
  );
  const toolbarSectionStyle = useMemo(
    () =>
      shouldApplyToolbarScale
        ? {
          borderRadius: getToolbarPx(8, 9),
          gap: getToolbarPx(3, 4),
          minHeight: getToolbarPx(28, 32),
          padding: `${getToolbarPx(2)} ${getToolbarPx(4, 5)}`,
        }
        : undefined,
    [getToolbarPx, shouldApplyToolbarScale]
  );
  const toolbarButtonStyle = useMemo(
    () =>
      shouldApplyToolbarScale
        ? {
          fontSize: getToolbarPx(12, 14),
          height: getToolbarPx(24, 28),
          minWidth: getToolbarPx(24, 32),
          padding: `0 ${getToolbarPx(7, 9)}`,
        }
        : undefined,
    [getToolbarPx, shouldApplyToolbarScale]
  );
  const toolbarTextStyle = useMemo(
    () =>
      shouldApplyToolbarScale
        ? {
          fontSize: getToolbarPx(11, 12),
          lineHeight: getToolbarPx(12, 16),
          padding: `0 ${getToolbarPx(3, 5)}`,
        }
        : undefined,
    [getToolbarPx, shouldApplyToolbarScale]
  );
  const toolbarDividerStyle = useMemo(
    () =>
      shouldApplyToolbarScale
        ? {
          height: getToolbarPx(14),
          margin: `0 ${getToolbarPx(2)}`,
        }
        : undefined,
    [getToolbarPx, shouldApplyToolbarScale]
  );

  // Wait until the preview viewport has two stable measurements so uncached
  // images do not reveal the browser's first layout correction as a flicker.
  useLayoutEffect(() => {
    if (!isImageReady) {
      hasCompletedInitialRenderRef.current = false;
      setIsInitialImageLayoutSettled(false);
      return undefined;
    }

    const ownerWindow = scrollContainerRef.current?.ownerDocument.defaultView ?? window;
    let frameId: number | null = null;
    let previousDimensions: ImageViewerDimensions | null = null;
    let attempts = 0;
    const measureUntilStable = () => {
      frameId = null;
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) {
        return;
      }

      const { height, width } = scrollContainer.getBoundingClientRect();
      const nextDimensions = {
        height: Math.max(1, Math.round(height)),
        width: Math.max(1, Math.round(width)),
      };
      const isStable = previousDimensions != null && areDimensionsEqual(previousDimensions, nextDimensions);

      attempts += 1;
      if (isStable || attempts >= 10) {
        hasCompletedInitialRenderRef.current = true;
        setIsInitialImageLayoutSettled(true);
        clearTimeout(resizeOverlayEnableTimeoutRef.current);
        resizeOverlayEnableTimeoutRef.current = setTimeout(() => {
          isImageResizeOverlayEnabledRef.current = true;
        }, IMAGE_INITIAL_RESIZE_OVERLAY_DELAY_MS);
        return;
      }

      previousDimensions = nextDimensions;
      frameId = ownerWindow.requestAnimationFrame(measureUntilStable);
    };

    frameId = ownerWindow.requestAnimationFrame(measureUntilStable);

    return () => {
      if (frameId != null) {
        ownerWindow.cancelAnimationFrame(frameId);
      }
    };
  }, [isImageReady]);

  const handleZoomOut = useCallback(() => {
    updateZoomScale(currentZoomScale => Math.max(IMAGE_MIN_ZOOM, currentZoomScale - IMAGE_ZOOM_STEP));
  }, [updateZoomScale]);

  const handleZoomIn = useCallback(() => {
    updateZoomScale(currentZoomScale => Math.min(IMAGE_MAX_ZOOM, currentZoomScale + IMAGE_ZOOM_STEP));
  }, [updateZoomScale]);

  const handleFitImage = useCallback(() => {
    updateZoomScale(() => 1);
  }, [updateZoomScale]);

  // Keep the same visual center after zoom changes so the image does not jump.
  useLayoutEffect(() => {
    const zoomAnchor = pendingZoomAnchorRef.current;
    const scrollContainer = scrollContainerRef.current;
    const imageContainer = getImageContainerElement(imageRef.current);
    if (!zoomAnchor || !scrollContainer || !imageContainer || !renderedDimensions || !viewportDimensions) {
      return undefined;
    }

    pendingZoomAnchorRef.current = null;
    const ownerWindow = scrollContainer.ownerDocument.defaultView ?? window;
    const applyZoomAnchor = () => {
      scrollContainer.scrollLeft = Math.max(
        0,
        imageContainer.offsetLeft +
          zoomAnchor.horizontalRatio * imageContainer.offsetWidth -
          scrollContainer.clientWidth / 2
      );
      scrollContainer.scrollTop = Math.max(
        0,
        imageContainer.offsetTop +
          zoomAnchor.verticalRatio * imageContainer.offsetHeight -
          scrollContainer.clientHeight / 2
      );
    };
    applyZoomAnchor();
    let secondFrameId: number | null = null;
    const frameId = ownerWindow.requestAnimationFrame(() => {
      applyZoomAnchor();
      secondFrameId = ownerWindow.requestAnimationFrame(applyZoomAnchor);
    });

    return () => {
      ownerWindow.cancelAnimationFrame(frameId);
      if (secondFrameId != null) {
        ownerWindow.cancelAnimationFrame(secondFrameId);
      }
    };
  }, [renderedDimensions, viewportDimensions]);

  const stopPanning = useCallback((event?: PointerEvent<HTMLDivElement>) => {
    const panSession = panSessionRef.current;
    if (!panSession) {
      return;
    }

    if (event?.currentTarget.hasPointerCapture(panSession.pointerId)) {
      event.currentTarget.releasePointerCapture(panSession.pointerId);
    }

    panSessionRef.current = null;
    setIsPanning(false);
  }, []);

  const handleImagePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!isPannable || event.button !== 0 || !scrollContainerRef.current) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (target?.closest('button, input, textarea, select, a, [role="button"]')) {
        return;
      }

      panSessionRef.current = {
        pointerId: event.pointerId,
        scrollLeft: scrollContainerRef.current.scrollLeft,
        scrollTop: scrollContainerRef.current.scrollTop,
        startX: event.clientX,
        startY: event.clientY,
      };
      setIsPanning(true);
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    [isPannable]
  );

  const handleImagePointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const panSession = panSessionRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!panSession || !scrollContainer) {
      return;
    }

    scrollContainer.scrollLeft = panSession.scrollLeft - (event.clientX - panSession.startX);
    scrollContainer.scrollTop = panSession.scrollTop - (event.clientY - panSession.startY);
    event.preventDefault();
  }, []);

  const handleImagePointerEnd = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (panSessionRef.current?.pointerId !== event.pointerId) {
        return;
      }

      stopPanning(event);
    },
    [stopPanning]
  );

  // End a pan session if the image returns to a non-scrollable fit state.
  useEffect(() => {
    if (!isPannable) {
      stopPanning();
    }
  }, [isPannable, stopPanning]);

  if (error) {
    return <ImageViewerStateContainer style={{ color: 'red' }}>Error: {error}</ImageViewerStateContainer>;
  }

  const hasImageSource = Boolean(base64Content || src);
  if (!imageData && !loading && !isSourceLoading && !hasImageSource) {
    return <ImageViewerStateContainer>No image available</ImageViewerStateContainer>;
  }

  return (
    <ImageViewerWrapper $animateOnMount={animateOnMount}>
      <FileViewerToolbar
        aria-hidden={!isImageVisible}
        aria-label="Image preview controls"
        style={{
          ...(toolbarStyle ?? {}),
          visibility: isImageVisible ? 'visible' : 'hidden',
        }}>
        <FileViewerToolbarSection style={toolbarSectionStyle}>
          <FileViewerToolbarText style={toolbarTextStyle}>
            {naturalDimensions?.width} × {naturalDimensions?.height}px
          </FileViewerToolbarText>
        </FileViewerToolbarSection>
        <FileViewerToolbarSection style={toolbarSectionStyle}>
          <FileViewerToolbarButton
            aria-label="Zoom out"
            disabled={zoomScale <= IMAGE_MIN_ZOOM}
            onClick={handleZoomOut}
            style={toolbarButtonStyle}
            type="button">
            −
          </FileViewerToolbarButton>
          <FileViewerToolbarText style={toolbarTextStyle}>{zoomPercentage}%</FileViewerToolbarText>
          <FileViewerToolbarButton
            aria-label="Zoom in"
            disabled={zoomScale >= IMAGE_MAX_ZOOM}
            onClick={handleZoomIn}
            style={toolbarButtonStyle}
            type="button">
            +
          </FileViewerToolbarButton>
          <FileViewerToolbarDivider aria-hidden="true" style={toolbarDividerStyle} />
          <FileViewerToolbarButton
            aria-label="Fit image to preview"
            disabled={zoomScale === 1}
            onClick={handleFitImage}
            style={toolbarButtonStyle}
            type="button">
            Fit
          </FileViewerToolbarButton>
        </FileViewerToolbarSection>
      </FileViewerToolbar>
      <ImageViewerScrollContainer
        ref={handleScrollContainerRef}
        data-panning={isPanning ? 'true' : undefined}
        $isPannable={isPannable}
        $isPanning={isPanning}
        $isResizing={isImageResizing}
        onPointerCancel={handleImagePointerEnd}
        onPointerDown={handleImagePointerDown}
        onPointerMove={handleImagePointerMove}
        onPointerUp={handleImagePointerEnd}>
        {imageData && (
          <ImageViewerCanvas
            $isHidden={!isImageVisible}
            $isResizing={isImageResizing}
            style={{
              minHeight:
                renderedDimensions && viewportDimensions
                  ? Math.max(viewportDimensions.height, renderedDimensions.height + IMAGE_VIEWER_PADDING)
                  : undefined,
              minWidth:
                renderedDimensions && viewportDimensions
                  ? Math.max(viewportDimensions.width, renderedDimensions.width + IMAGE_VIEWER_PADDING)
                  : undefined,
            }}>
            <ImageViewerContainer
              style={{
                height: renderedDimensions?.height ?? 0,
                visibility: isImageVisible ? 'visible' : 'hidden',
                width: renderedDimensions?.width ?? 0,
              }}>
              <ImageViewerImage
                ref={handleImageRef}
                src={imageData}
                alt={alt}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </ImageViewerContainer>
          </ImageViewerCanvas>
        )}
        {(loading || isSourceLoading || !isImageVisible) && (
          <FileViewerInitialLoadOverlay aria-busy="true" aria-live="polite">
            <FileViewerLoadingSpinner size={28} thickness={4} />
            <span>Preparing image preview...</span>
          </FileViewerInitialLoadOverlay>
        )}
      </ImageViewerScrollContainer>
      {isImageResizing && (
        <FileViewerResizeOverlay aria-busy="true" aria-live="polite">
          <FileViewerLoadingSpinner size={24} thickness={4} />
          <span>Resizing preview...</span>
        </FileViewerResizeOverlay>
      )}
    </ImageViewerWrapper>
  );
};

export default ImageViewer;
